import { useState } from 'react';
import DashboardNavigation from './DashboardNavigation';
import Footer from './Footer';
import { policySystemMessages } from '../content/policySystemMessages';
import { usePolicySystemStatus } from '../context/PolicySystemStatusProvider';

const primaryLinkClassName =
  'font-body font-semibold flex gap-[6px] items-center justify-center overflow-clip rounded-md shrink-0 w-full bg-background-brand-solid text-text-white shadow-xs px-[18px] py-[12px] text-md leading-md no-underline hover:opacity-95';

type PolicySystemUnavailablePageProps = {
  /** Optional API correlation ID from a failed request. */
  correlationId?: string;
  onTryAgain?: () => void | Promise<void>;
};

/**
 * Full-page block when online policy services cannot be reached (Mode C).
 */
export default function PolicySystemUnavailablePage({
  correlationId,
  onTryAgain,
}: PolicySystemUnavailablePageProps) {
  const { refresh, incidentRef } = usePolicySystemStatus();
  const [retrying, setRetrying] = useState(false);
  const reference = correlationId ?? incidentRef;

  const handleTryAgain = async () => {
    setRetrying(true);
    try {
      if (onTryAgain) {
        await onTryAgain();
      } else {
        await refresh();
        window.location.reload();
      }
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="bg-background-tertiary flex flex-col min-h-screen w-full">
      <DashboardNavigation />
      <div className="flex flex-col flex-1 w-full px-[16px] md:px-[32px] py-[40px] md:py-[64px] max-w-container-desktop mx-auto">
        <div className="bg-background-primary border border-border-primary border-solid flex flex-col gap-[20px] items-start px-[24px] py-[32px] relative rounded-2xl shadow-xs w-full max-w-[640px] mx-auto">
          <p className="font-display font-semibold text-display-xs leading-display-xs text-text-primary-900 m-0">
            {policySystemMessages.pageTitle}
          </p>
          <p className="font-body font-regular text-md leading-md text-text-secondary-700 m-0">
            {policySystemMessages.pageBody}
          </p>
          <ul className="font-body font-regular text-sm leading-sm text-text-secondary-700 m-0 pl-[20px] flex flex-col gap-[8px]">
            <li>{policySystemMessages.pageBulletCover}</li>
            <li>{policySystemMessages.pageBulletUrgentClaim}</li>
            <li>{policySystemMessages.pageBulletPolicyChange}</li>
            <li>{policySystemMessages.pageBulletLinkPolicy}</li>
          </ul>
          <div className="flex flex-col sm:flex-row gap-[12px] w-full pt-[8px]">
            <a href={policySystemMessages.supportPhoneHref} className={primaryLinkClassName}>
              Call {policySystemMessages.supportPhone}
            </a>
            <button
              type="button"
              onClick={() => void handleTryAgain()}
              disabled={retrying}
              className="w-full sm:w-auto px-[16px] py-[12px] rounded-md border border-border-primary bg-background-primary font-body font-semibold text-sm text-text-primary-900 hover:bg-background-secondary disabled:opacity-60"
            >
              {retrying ? 'Checking…' : policySystemMessages.tryAgain}
            </button>
          </div>
          {reference ? (
            <p className="font-body font-regular text-xs text-text-tertiary-600 m-0">
              {policySystemMessages.referenceLabel}:{' '}
              <span className="font-mono select-all">{reference}</span>
            </p>
          ) : null}
        </div>
      </div>
      <Footer />
    </div>
  );
}
